import {
  updateProfileImage as updateProfileImageCall,
  updateHeaderImage as updateHeaderImageCall,
  updatePassword as updatePasswordCall,
  updateEmail as updateEmailCall,
  updateAboutMe as updateAboutMeCall,
  updateCollectionPrivacy as updateCollectionPrivacyCall,
  updateCollectionFinancialStatsPrivacy as updateCollectionFinancialStatsPrivacyCall,
  updateCollectionNumericStatsPrivacy as updateCollectionNumericStatsPrivacyCall,
  updateMyDetails as updateMyDetailsCall,
  getUserDetails as getUserDetailsCall,
  getSocialNetworks as getSocialNetworksCall,
  updateSocialNetworks as updateSocialNetworksCall,
  removeSocialNetworks as removeSocialNetworksCall,
  searchUsers as searchUsersCall,
} from "./api/userService";
import NotificationService from "../components/Notification/notificationService";
import LoginService from "./loginService";
import { Collector, Image } from "./shirts/types";
import { SocialNetworksData, UserDataUpdate } from "../types/authTypes";
import { User } from "../entities/User";

export default function UserService() {
  const { error: errorNotification } = NotificationService();
  const { setLogout } = LoginService();

  const handleError = (status: number, description: string) => {
    if (status === 401) {
      setLogout();
    } else {
      errorNotification(description);
    }
  };

  const updateProfileImage = async (image: Image) => {
    let response = await updateProfileImageCall(image).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    let result: Image | undefined = undefined;
    if (response?.status === 200) {
      result = response.data.data;
    }
    return result;
  };
  const updateHeaderImage = async (image: Image) => {
    let response = await updateHeaderImageCall(image).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    let result: Image | undefined = undefined;
    if (response?.status === 200) {
      result = response.data.data;
    }
    return result;
  };
  const updatePassword = async (
    current: string,
    password: string,
    confirmation: string
  ) => {
    let response = await updatePasswordCall(
      current,
      password,
      confirmation
    ).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    return response?.status === 200;
  };
  const updateEmail = async (email: string) => {
    let response = await updateEmailCall(email).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    return response?.status === 200;
  };
  const updateAboutMe = async (about: string) => {
    let response = await updateAboutMeCall(about).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    return response?.status === 200;
  };
  const updateMyDetails = async (details: UserDataUpdate) => {
    let response = await updateMyDetailsCall(details).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    return response?.status === 200;
  };
  const updateCollectionPrivacy = async (isPrivate: boolean) => {
    let response = await updateCollectionPrivacyCall(isPrivate).catch(
      function (error) {
        const status = error.response.status;
        const description = error.response.data.error_description;
        handleError(status, description);
      }
    );

    return response?.status === 200;
  };
  const updateCollectionFinancialStatsPrivacy = async (isPublic: boolean) => {
    let response = await updateCollectionFinancialStatsPrivacyCall(
      isPublic
    ).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    return response?.status === 204;
  };
  const updateCollectionNumericStatsPrivacy = async (isPublic: boolean) => {
    let response = await updateCollectionNumericStatsPrivacyCall(
      isPublic
    ).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    return response?.status === 204;
  };

  const getUserDetails = async (userId: number) => {
    let response = await getUserDetailsCall(userId).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    if (response?.data) {
      const userData = new User(
        response.data.data.id,
        undefined,
        response.data.data.username,
        undefined,
        undefined,
        undefined,
        response.data.data.role?.id,
        response.data.data.email,
        response.data.data.profile_image,
        response.data.data.header_image,
        response.data.data.userData,
        response.data.data.collection,
        response.data.data.social_networks,
        response.data.data.item_count
      );

      return userData;
    } else {
      return undefined;
    }
  };

  const getSocialNetworks = async () => {
    let response = await getSocialNetworksCall().catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    if (response?.data) {
      const socialNetworks: Array<SocialNetworksData> = response.data.data;
      return socialNetworks;
    }
    return [];
  };
  const updateSocialNetworks = async (snId: number, snUrl: string) => {
    let response = await updateSocialNetworksCall(snId, snUrl).catch(
      function (error) {
        const status = error.response.status;
        const description = error.response.data.error_description;
        handleError(status, description);
      }
    );

    return response?.status === 200;
  };
  const removeSocialNetworks = async (snId: number) => {
    let response = await removeSocialNetworksCall(snId).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    return response?.status === 200;
  };

  const searchUsers = async (
    text: string | undefined,
    pageNum?: number,
    pageSize?: number
  ) => {
    let response = await searchUsersCall(text, pageNum, pageSize).catch(
      function (error) {
        const status = error.response.status;
        const description = error.response.data.error_description;
        handleError(status, description);
      }
    );

    if (response?.data) {
      const collectors: Array<Collector> = response.data.data;
      return {
        collectors,
        meta: response.data.meta,
      };
    }
    return [];
  };

  return {
    updateProfileImage,
    updateHeaderImage,
    updatePassword,
    updateEmail,
    updateAboutMe,
    updateCollectionPrivacy,
    updateCollectionFinancialStatsPrivacy,
    updateCollectionNumericStatsPrivacy,
    updateMyDetails,
    getUserDetails,
    getSocialNetworks,
    updateSocialNetworks,
    removeSocialNetworks,
    searchUsers,
  };
}
